import axios from 'axios';
import { apiUrl } from '@/env';
import {
  IUserProfile,
  IUserProfileUpdate,
  IUserProfileCreate,
  ITemplate,
  ITemplateCreate,
  IServiceCreate,
  IService,
  IServiceUpdate,
  ITemplateUpdate,
  IMessage,
  IMessageParseResult,
  IProxyError,
  IProxyUserBansStatistics,
  IProxyErrorCreate,
  IProxyErrorUpdate,
  IProxyUserBans,
  ISenderCreate,
  ISenderUpdate,
  ISender,
  IPriority,
  IPriorityUpdate,
  IProxyUserBanCreate,
  IMNO,
  IMNOUpdate,
  ITemplates,
  IMNOCreate,
  IMNOMaskCreate,
  IMNOMasks,
  IMNOSenderUpdate,
  IMNOSenderCreate,
  IMNOSender,
  ITag,
  IServiceBanPolicyCreateUpdate,
  IServiceBanPolicy,
} from './interfaces';
import { ICheckTemplateProxy } from '@/interfaces/utilities';

function authHeaders(token: string) {
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
}

export const api = {
  async logInGetToken(username: string, password: string) {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);

    return axios.post(`${apiUrl}/api/v1/login/access-token`, params);
  },
  async getMe(token: string) {
    return axios.get<IUserProfile>(`${apiUrl}/api/v1/users/me`, authHeaders(token));
  },
  async updateMe(token: string, data: IUserProfileUpdate) {
    return axios.patch<IUserProfile>(`${apiUrl}/api/v1/users/me`, data, authHeaders(token));
  },
  async getUsers(token: string) {
    return axios.get<IUserProfile[]>(`${apiUrl}/api/v1/users/`, authHeaders(token));
  },
  async getUser(token: string, id: number) {
    return axios.get<IUserProfile>(`${apiUrl}/api/v1/users/${id}`, authHeaders(token));
  },
  async updateUser(token: string, userId: number, data: IUserProfileUpdate) {
    return axios.patch(`${apiUrl}/api/v1/users/${userId}`, data, authHeaders(token));
  },
  async createUser(token: string, data: IUserProfileCreate) {
    return axios.post(`${apiUrl}/api/v1/users/`, data, authHeaders(token));
  },
  async deleteUser(token: string, userId: number) {
    return axios.delete(`${apiUrl}/api/v1/users/${userId}`, authHeaders(token));
  },
  async deleteUsers(token: string, usersIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/users/delete/`, { data: usersIds, ...authHeaders(token) });
  },

  async createTemplate(token: string, data: ITemplateCreate) {
    return axios.post(`${apiUrl}/api/v1/templates/`, data, authHeaders(token));
  },
  async updateTemplate(token: string, templateId: number, data: ITemplateUpdate) {
    return axios.patch(`${apiUrl}/api/v1/templates/${templateId}`, data, authHeaders(token));
  },
  async getTemplate(token: string, id: number) {
    return axios.get<ITemplate>(`${apiUrl}/api/v1/templates/${id}`, authHeaders(token));
  },
  async getTemplates(
    token: string,
    search: string,
    active: boolean | null,
    priorityId: number | null,
    senderId: number | null,
    serviceId: number | null,
    mnoBanId: number | null,
    tagId: number | null,
    skip: number,
    limit: number,
    orderBy: string,
    descending: boolean,
  ) {
    return axios.get<ITemplates>(`${apiUrl}/api/v1/templates/`, {
      params: {
        search,
        active,
        priority_id: priorityId,
        sender_id: senderId,
        service_id: serviceId,
        mno_ban_id: mnoBanId,
        tag_id: tagId,
        skip,
        limit,
        order_by: orderBy,
        descending,
      },
      ...authHeaders(token),
    });
  },
  async deleteTemplate(token: string, templateId: number) {
    return axios.delete(`${apiUrl}/api/v1/templates/${templateId}`, authHeaders(token));
  },
  async deleteTemplates(token: string, templateIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/templates/delete/`, { data: templateIds, ...authHeaders(token) });
  },
  async parseMessage(token: string, payload: IMessage) {
    return axios.post<IMessageParseResult>(`${apiUrl}/api/v1/templates/parse_message`, payload, authHeaders(token));
  },

  async createService(token: string, data: IServiceCreate) {
    return axios.post(`${apiUrl}/api/v1/services/`, data, authHeaders(token));
  },
  async getService(token: string, id: number) {
    return axios.get<IService>(`${apiUrl}/api/v1/services/${id}`, authHeaders(token));
  },
  async getServices(token: string) {
    return axios.get<IService[]>(`${apiUrl}/api/v1/services/`, authHeaders(token));
  },
  async updateService(token: string, serviceId: number, data: IServiceUpdate) {
    return axios.patch(`${apiUrl}/api/v1/services/${serviceId}`, data, authHeaders(token));
  },
  async deleteService(token: string, serviceId: number) {
    return axios.delete(`${apiUrl}/api/v1/services/${serviceId}`, authHeaders(token));
  },
  async deleteServices(token: string, serviceIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/services/delete/`, { data: serviceIds, ...authHeaders(token) });
  },

  async createSender(token: string, data: ISenderCreate) {
    return axios.post(`${apiUrl}/api/v1/senders/`, data, authHeaders(token));
  },
  async getSender(token: string, id: number) {
    return axios.get<ISender>(`${apiUrl}/api/v1/senders/${id}`, authHeaders(token));
  },
  async getSenders(token: string) {
    return axios.get<ISender[]>(`${apiUrl}/api/v1/senders/`, authHeaders(token));
  },
  async updateSender(token: string, senderId: number, data: ISenderUpdate) {
    return axios.patch(`${apiUrl}/api/v1/senders/${senderId}`, data, authHeaders(token));
  },
  async deleteSender(token: string, senderId: number) {
    return axios.delete(`${apiUrl}/api/v1/senders/${senderId}`, authHeaders(token));
  },
  async deleteSenders(token: string, senderId: number[]) {
    return axios.delete(`${apiUrl}/api/v1/senders/delete/`, { data: senderId, ...authHeaders(token) });
  },

  async createProxyError(token: string, data: IProxyErrorCreate) {
    return axios.post(`${apiUrl}/api/v1/proxy_errors/`, data, authHeaders(token));
  },
  async getProxyError(token: string, id: number) {
    return axios.get<IProxyError>(`${apiUrl}/api/v1/proxy_errors/${id}`, authHeaders(token));
  },
  async getProxyErrors(token: string, serviceId: number | null) {
    return axios.get<IProxyError[]>(`${apiUrl}/api/v1/proxy_errors/`, {
      params: { service_id: serviceId },
      ...authHeaders(token),
    });
  },
  async updateProxyError(token: string, proxyErrorId: number, data: IProxyErrorUpdate) {
    return axios.patch(`${apiUrl}/api/v1/proxy_errors/${proxyErrorId}`, data, authHeaders(token));
  },
  async deleteProxyError(token: string, proxyErrorId: number) {
    return axios.delete(`${apiUrl}/api/v1/proxy_errors/${proxyErrorId}`, authHeaders(token));
  },
  async deleteProxyErrors(token: string, proxyErrorsIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/proxy_errors/delete/`, { data: proxyErrorsIds, ...authHeaders(token) });
  },

  async createProxyUserBan(token: string, data: IProxyUserBanCreate) {
    return axios.post(`${apiUrl}/api/v1/proxy_user_bans/`, data, authHeaders(token));
  },
  async getProxyUserBans(
    token: string,
    skip: number,
    limit: number,
    orderBy: string,
    descending: boolean,
    phoneQuery: string,
  ) {
    return axios.get<IProxyUserBans>(`${apiUrl}/api/v1/proxy_user_bans/`, {
      params: { skip, limit, order_by: orderBy, descending, phone_query: phoneQuery },
      ...authHeaders(token),
    });
  },
  async deleteProxyUserBan(token: string, proxyUserBanId: number) {
    return axios.delete(`${apiUrl}/api/v1/proxy_user_bans/${proxyUserBanId}`, authHeaders(token));
  },
  async deleteProxyUserBans(token: string, proxyUserBanIds: number[]) {
    return axios.delete(`${apiUrl}/api/v1/proxy_user_bans/delete/`, { data: proxyUserBanIds, ...authHeaders(token) });
  },
  async getProxyUserBansStatistics(token: string) {
    return axios.get<IProxyUserBansStatistics[]>(`${apiUrl}/api/v1/proxy_user_bans/statistics`, authHeaders(token));
  },

  async getPriorities(token: string) {
    return axios.get<IPriority[]>(`${apiUrl}/api/v1/priorities/`, authHeaders(token));
  },
  async updatePriority(token: string, priorityId: number, data: IPriorityUpdate) {
    return axios.patch(`${apiUrl}/api/v1/priorities/${priorityId}`, data, authHeaders(token));
  },

  async checkTemplateProxy(token: string, data: ICheckTemplateProxy) {
    return axios.post(`${apiUrl}/api/v1/util/check_template_proxy`, data, authHeaders(token));
  },

  async getMNO(token: string, mnoId: number) {
    return axios.get(`${apiUrl}/api/v1/mnos/${mnoId}`, authHeaders(token));
  },
  async getMNOs(token: string) {
    return axios.get<IMNO[]>(`${apiUrl}/api/v1/mnos/`, authHeaders(token));
  },
  async updateMNO(token: string, mnoId: number, data: IMNOUpdate) {
    return axios.patch(`${apiUrl}/api/v1/mnos/${mnoId}`, data, authHeaders(token));
  },
  async createMNO(token: string, data: IMNOCreate) {
    return axios.post(`${apiUrl}/api/v1/mnos/`, data, authHeaders(token));
  },

  async getMNOMasks(token: string, skip: number, limit: number, mnoId: number) {
    return axios.get<IMNOMasks>(`${apiUrl}/api/v1/mnos/${mnoId}/masks/`, {
      params: { skip, limit },
      ...authHeaders(token),
    });
  },
  async checkMNOMask(token: string, phone: string) {
    return axios.get(`${apiUrl}/api/v1/util/check-mno-mask`, { params: { phone }, ...authHeaders(token) });
  },
  async deleteMNOMasks(token: string, mnoId: number, ids: number[]) {
    return axios.delete(`${apiUrl}/api/v1/mnos/${mnoId}/masks/delete/`, { data: ids, ...authHeaders(token) });
  },
  async createMNOMask(token: string, mnoId: number, data: IMNOMaskCreate) {
    return axios.post(`${apiUrl}/api/v1/mnos/${mnoId}/masks/`, data, authHeaders(token));
  },

  async getMNOSenders(token: string, mnoId: number) {
    return axios.get<IMNOSender[]>(`${apiUrl}/api/v1/mnos/${mnoId}/senders/`, authHeaders(token));
  },
  async deleteMNOSenders(token: string, mnoId: number, ids: number[]) {
    return axios.delete(`${apiUrl}/api/v1/mnos/${mnoId}/senders/delete/`, { data: ids, ...authHeaders(token) });
  },
  async createMNOSender(token: string, mnoId: number, data: IMNOSenderCreate) {
    return axios.post(`${apiUrl}/api/v1/mnos/${mnoId}/senders/`, data, authHeaders(token));
  },
  async updateMNOSender(token: string, mnoId: number, mnoSenderId: number, data: IMNOSenderUpdate) {
    return axios.patch(`${apiUrl}/api/v1/mnos/${mnoId}/senders/${mnoSenderId}`, data, authHeaders(token));
  },

  async getTags(token: string) {
    return axios.get<ITag[]>(`${apiUrl}/api/v1/tags/`, authHeaders(token));
  },
  async suggestTags(token: string, text?: string) {
    return axios.get<ITag[]>(`${apiUrl}/api/v1/tags/`, { params: { text }, ...authHeaders(token) });
  },

  async createServiceBanPolicy(token: string, data: IServiceBanPolicyCreateUpdate) {
    return axios.post(`${apiUrl}/api/v1/service-ban-policies/`, data, authHeaders(token));
  },
  async getServiceBanPolicy(token: string, id: number) {
    return axios.get<IServiceBanPolicy>(`${apiUrl}/api/v1/service-ban-policies/${id}`, authHeaders(token));
  },
  async getServiceBanPolicies(token: string) {
    return axios.get<IServiceBanPolicy[]>(`${apiUrl}/api/v1/service-ban-policies/`, authHeaders(token));
  },
  async updateServiceBanPolicy(token: string, serviceBanPolicyId: number, data: IServiceBanPolicyCreateUpdate) {
    return axios.patch(`${apiUrl}/api/v1/service-ban-policies/${serviceBanPolicyId}`, data, authHeaders(token));
  },
  async deleteServiceBanPolicy(token: string, serviceBanPolicyId: number) {
    return axios.delete(`${apiUrl}/api/v1/service-ban-policies/${serviceBanPolicyId}`, authHeaders(token));
  },
  async deleteServiceBanPolicies(token: string, serviceBanPolicyId: number[]) {
    return axios.delete(`${apiUrl}/api/v1/service-ban-policies/delete/`, {
      data: serviceBanPolicyId,
      ...authHeaders(token),
    });
  },
};
